import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { cn } from '@noor/lib/cn';
import { MenuCloseIcon } from './menu-icons';

function Dialog(props: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

function DialogTrigger(props: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal(props: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose(props: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

function DialogOverlay({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(
        'fixed inset-0 z-50 bg-overlay-scrim data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0',
        className,
      )}
      {...props}
    />
  );
}

function DialogContent({
  className,
  children,
  showClose = true,
  focusOnOpen = true,
  closeOnInteractOutside = false,
  onOpenAutoFocus,
  onInteractOutside,
  onPointerDownOutside,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  /** Render the built-in top-right close button. Defaults to true. */
  showClose?: boolean;
  /**
   * Whether a click/tap outside the dialog dismisses it. Defaults to FALSE:
   * dialogs that offer an explicit Cancel/close affordance must not close on
   * an accidental backdrop click and lose the user's input. Opt in with `true`
   * for purely informational dialogs. Escape is unaffected.
   */
  closeOnInteractOutside?: boolean;
  /**
   * Whether opening moves focus to the first focusable child.
   *
   * Defaults to TRUE (Radix's behaviour) so existing dialogs are unchanged.
   * Pass `false` on a form opened over existing data: Radix lands on the first
   * input and SELECTS its text, which reads as an invitation to rewrite a
   * field the user didn't come to touch — and is one keystroke from doing it.
   *
   * Focus still moves to the dialog container, so it stays keyboard-reachable
   * and the focus trap holds; Tab from there reaches the first field.
   *
   * The shared `Modal` shell already defaults this off — this prop is for
   * dialogs built on the primitives directly. An explicit `onOpenAutoFocus`
   * wins over it.
   */
  focusOnOpen?: boolean;
}) {
  const handleOpenAutoFocus =
    onOpenAutoFocus ??
    (focusOnOpen
      ? undefined
      : (event: Event) => {
          event.preventDefault();
          // Radix focuses the first focusable child; without a target of our
          // own the focus would fall to <body> and leave the trap.
          (event.currentTarget as HTMLElement | null)?.focus();
        });

  // Outside clicks never dismiss by default (see `closeOnInteractOutside`).
  // Still invoke the caller's handlers so custom logic keeps running.
  const handleInteractOutside: React.ComponentProps<
    typeof DialogPrimitive.Content
  >['onInteractOutside'] = closeOnInteractOutside
    ? onInteractOutside
    : (event) => {
        event.preventDefault();
        onInteractOutside?.(event);
      };
  const handlePointerDownOutside: React.ComponentProps<
    typeof DialogPrimitive.Content
  >['onPointerDownOutside'] = closeOnInteractOutside
    ? onPointerDownOutside
    : (event) => {
        event.preventDefault();
        onPointerDownOutside?.(event);
      };

  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        data-slot="dialog-content"
        {...(handleOpenAutoFocus ? { onOpenAutoFocus: handleOpenAutoFocus } : {})}
        {...(handleInteractOutside ? { onInteractOutside: handleInteractOutside } : {})}
        {...(handlePointerDownOutside
          ? { onPointerDownOutside: handlePointerDownOutside }
          : {})}
        // Lets the header reserve a gutter for the absolutely-positioned close
        // button — see `DialogHeader`. Set only when that button is rendered,
        // so a `showClose={false}` dialog keeps the full width for its title.
        data-has-close={showClose ? '' : undefined}
        // `outline-none`: the card is a focus CONTAINER, not a control. Radix
        // gives it `tabIndex={-1}` and the shell focuses it on open (so focus
        // never falls to <body> and out of the trap) — and the base layer's
        // `* { outline-ring/50 }` then had the browser paint its default focus
        // outline around the whole dialog in brand orange. Suppressing it costs
        // nothing in a11y: the ring communicated nothing (the container is not
        // interactive) and every focusable child keeps its own.
        className={cn(
          // Card metrics resolve through `--modal-*` sizing handles so a host
          // app retunes every dialog/modal from its own stylesheet.
          'fixed top-[50%] left-[50%] z-50 grid max-h-[calc(100%-2rem)] w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-[var(--modal-gap)] overflow-y-auto rounded-[var(--modal-radius)] border border-surface-border bg-surface-bg p-[var(--modal-padding)] text-surface-fg shadow-lg duration-200 outline-none data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 sm:max-w-[var(--modal-max-width)]',
          className,
        )}
        {...props}
      >
        {children}
        {showClose ? (
          <DialogPrimitive.Close className="absolute top-2.5 right-6 flex cursor-pointer items-center justify-center rounded-lg p-2.5 text-foreground transition-colors hover:bg-muted focus-visible:ring-[3px] focus-visible:ring-ring/40 focus-visible:outline-none disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0">
            <MenuCloseIcon className="size-6" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        ) : null}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
}

/**
 * The close button is absolutely positioned in the content's top-right corner
 * and so takes no space in the flow — a title long enough to wrap ran straight
 * underneath it. The header reserves that corner (44px button + 24px inset +
 * 8px breathing room) whenever a close button is actually rendered, keyed off
 * `DialogContent`'s `data-has-close`.
 */
function DialogHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-header"
      className={cn(
        'flex flex-col gap-[var(--modal-header-gap)] text-center sm:text-left',
        '[[data-has-close]_&]:pr-[76px]',
        className,
      )}
      {...props}
    />
  );
}

function DialogFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        'flex items-center justify-end gap-[var(--modal-footer-gap)]',
        className,
      )}
      {...props}
    />
  );
}

function DialogTitle({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn(
        'text-[length:var(--modal-title-font-size)] leading-normal font-[var(--modal-title-font-weight)] tracking-[var(--modal-title-tracking)]',
        className,
      )}
      {...props}
    />
  );
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn(
        'text-[length:var(--modal-description-font-size)] leading-[var(--modal-description-line-height)] text-muted-foreground',
        className,
      )}
      {...props}
    />
  );
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
